import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRealtimeEvent } from '../../hooks/useRealtimeEvent';
import useAppointmentStore from '../../stores/appointmentStore';
import RoleTaskBadge from '../procedures/widget/RoleTaskBadge';

/**
 * ConfirmAppointmentsWidget — floating right-edge counter badge that alerts the
 * receptionist/admin when a doctor's BLOCKING calendar block overlaps already-
 * BOOKED appointments.
 *
 * Closed: a purple 64px bubble with the impacted-patient count (mirrors the
 * "Action pending" badge). Open: a panel grouped by doctor, proximity-coloured
 * (red = today/tomorrow, amber = within a week, none = >7d), with
 * Reschedule / Cancel / Clear per patient. Auto-hides at count 0 (re-derived
 * from the server on load → survives refresh). Appointments purple (#a855f7).
 */

const PROX_RANK = { red: 0, amber: 1 };
const rankOf = (p) => (p in PROX_RANK ? PROX_RANK[p] : 2);

function proximityStyle(prox, isDark) {
  if (prox === 'red') {
    return {
      dot: '#dc2626',
      chipBg: isDark ? 'rgba(220,38,38,0.18)' : '#fee2e2',
      chipText: isDark ? '#fecaca' : '#991b1b',
    };
  }
  if (prox === 'amber') {
    return {
      dot: '#d97706',
      chipBg: isDark ? 'rgba(217,119,6,0.18)' : '#fef3c7',
      chipText: isDark ? '#fcd34d' : '#92400e',
    };
  }
  return {
    dot: isDark ? '#64748b' : '#94a3b8',
    chipBg: isDark ? 'rgba(100,116,139,0.18)' : '#e2e8f0',
    chipText: isDark ? '#cbd5e1' : '#475569',
  };
}

function fmtTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch {
    return iso;
  }
}

export default function ConfirmAppointmentsWidget({ onReschedule, onNavigateToAppointments }) {
  const impacted = useAppointmentStore((s) => s.impactedAppointments);
  const fetchImpacted = useAppointmentStore((s) => s.fetchImpacted);
  const dismissImpact = useAppointmentStore((s) => s.dismissImpact);
  const cancel = useAppointmentStore((s) => s.cancel);

  const [doctorFilter, setDoctorFilter] = useState('');
  const [expanded, setExpanded] = useState({});      // doctor_member_id → bool
  const [confirmCancel, setConfirmCancel] = useState(null); // appointment_id awaiting confirm
  const [busyId, setBusyId] = useState(null);

  // Anchor the bubble just below the app header (keeps it clear of the
  // receptionist's vertically-centered "Action pending" bubble).
  const [anchorPx, setAnchorPx] = useState(96);
  useEffect(() => {
    const header = document.querySelector('.admin-header');
    if (!header || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setAnchorPx(Math.round(header.getBoundingClientRect().height + 12));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(header);
    return () => ro.disconnect();
  }, []);

  // ── Initial load + realtime refetch (debounced) ──────────────────
  useEffect(() => {
    fetchImpacted();
  }, [fetchImpacted]);

  const debounceRef = useRef(null);
  useRealtimeEvent(
    [
      'APPT_IMPACT_CHANGED',
      'CALENDAR_BLOCK_CHANGED',
      'APPOINTMENT_CREATED',
      'APPOINTMENT_UPDATED',
      'APPOINTMENT_CANCELLED',
      'APPOINTMENT_CHECKED_IN',
    ],
    () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchImpacted(), 400);
    }
  );
  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  // ── Group by doctor, sort by proximity then datetime ─────────────
  const doctorGroups = useMemo(() => {
    const byDoctor = new Map();
    for (const imp of impacted) {
      const id = imp.doctor_member_id;
      if (!byDoctor.has(id)) {
        byDoctor.set(id, { id, name: imp.doctor_name || 'Doctor', items: [] });
      }
      byDoctor.get(id).items.push(imp);
    }
    const groups = Array.from(byDoctor.values());
    for (const g of groups) {
      g.items.sort((a, b) =>
        `${a.appointment_date} ${a.appointment_time}`.localeCompare(
          `${b.appointment_date} ${b.appointment_time}`
        )
      );
      // Nearest impact drives the group's proximity + sort key.
      g.rank = Math.min(...g.items.map((i) => rankOf(i.proximity)));
      g.nearestDate = g.items.reduce(
        (min, i) => (i.occ_date < min ? i.occ_date : min),
        g.items[0].occ_date
      );
    }
    groups.sort((a, b) => a.rank - b.rank || a.nearestDate.localeCompare(b.nearestDate));
    return groups;
  }, [impacted]);

  const visibleGroups = useMemo(
    () => (doctorFilter ? doctorGroups.filter((g) => g.id === doctorFilter) : doctorGroups),
    [doctorGroups, doctorFilter]
  );

  // Doctor groups start collapsed — the receptionist expands a doctor to see patients.

  const handleCancel = async (imp) => {
    if (confirmCancel !== imp.appointment_id) {
      setConfirmCancel(imp.appointment_id);
      return;
    }
    setBusyId(imp.appointment_id);
    try {
      await cancel(imp.appointment_id);
    } catch (_) { /* surfaced by store */ }
    setConfirmCancel(null);
    setBusyId(null);
    fetchImpacted();
  };

  const handleClear = (imp) => {
    dismissImpact(imp.appointment_id, imp.block_id);
  };

  const total = impacted?.length || 0;
  const subtitle = total
    ? `${doctorGroups.length} doctor${doctorGroups.length === 1 ? '' : 's'} · ${total} appt${total === 1 ? '' : 's'}`
    : null;

  const renderBody = ({ palette }) => {
    const isDark = palette.bg !== '#ffffff';
    return (
      <>
        {/* Doctor filter */}
        {doctorGroups.length > 1 && (
          <select
            value={doctorFilter}
            onChange={(e) => setDoctorFilter(e.target.value)}
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              padding: '0.3125rem 0.5rem',
              borderRadius: '0.5rem',
              border: `1px solid ${palette.border}`,
              background: palette.bgAlt,
              color: palette.text,
              cursor: 'pointer',
            }}
          >
            <option value="">All doctors</option>
            {doctorGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} ({g.items.length})
              </option>
            ))}
          </select>
        )}

        {visibleGroups.map((g) => {
          const isOpen = !!expanded[g.id];
          const gp = proximityStyle(g.rank === 0 ? 'red' : g.rank === 1 ? 'amber' : null, isDark);
          return (
            <div key={g.id}>
              {/* Doctor header */}
              <button
                onClick={() => setExpanded((e) => ({ ...e, [g.id]: !e[g.id] }))}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.625rem',
                  borderRadius: '0.625rem',
                  border: `1px solid ${palette.border}`,
                  background: palette.bgAlt,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: '0.625rem', color: palette.muted }}>{isOpen ? '▼' : '▶'}</span>
                <span style={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', background: gp.dot, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '0.8125rem', fontWeight: 600, color: palette.text }}>
                  {g.name}
                </span>
                <span style={{ fontSize: '0.6875rem', fontWeight: 600, padding: '0.125rem 0.5rem', borderRadius: '0.5rem', background: gp.chipBg, color: gp.chipText }}>
                  {g.items.length} impacted
                </span>
              </button>

              {/* Patient rows */}
              {isOpen && (
                <div style={{ padding: '0.375rem 0 0.125rem 0.25rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {g.items.map((imp) => {
                    const p = proximityStyle(imp.proximity, isDark);
                    const confirming = confirmCancel === imp.appointment_id;
                    const busy = busyId === imp.appointment_id;
                    return (
                      <div
                        key={`${imp.appointment_id}-${imp.block_id}-${imp.occ_date}`}
                        style={{
                          padding: '0.5rem 0.625rem',
                          borderRadius: '0.5rem',
                          border: `1px solid ${palette.border}`,
                          background: palette.bg,
                        }}
                      >
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: palette.text, display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                          <span style={{ width: '0.4375rem', height: '0.4375rem', borderRadius: '50%', background: p.dot, flexShrink: 0 }} />
                          {imp.patient_name || 'Patient'}
                        </div>
                        <div style={{ fontSize: '0.6875rem', color: palette.muted, margin: '0.125rem 0 0.375rem' }}>
                          {imp.patient_mobile ? `${imp.patient_mobile} · ` : ''}
                          {fmtDate(imp.appointment_date)} {fmtTime(imp.appointment_time)}
                          {imp.block_label ? ` · vs ${imp.block_label}` : ''}
                        </div>
                        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => onReschedule && onReschedule(imp)}
                            disabled={busy}
                            style={{
                              fontSize: '0.6875rem', fontWeight: 600, padding: '0.3125rem 0.625rem',
                              borderRadius: '0.4375rem', border: 'none',
                              background: palette.accent, color: '#ffffff',
                              cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
                            }}
                          >
                            Reschedule
                          </button>
                          <button
                            onClick={() => handleCancel(imp)}
                            disabled={busy}
                            style={{
                              fontSize: '0.6875rem', fontWeight: 600, padding: '0.3125rem 0.625rem',
                              borderRadius: '0.4375rem',
                              border: `1px solid ${confirming ? '#dc2626' : (isDark ? '#7f1d1d' : '#fca5a5')}`,
                              background: confirming ? '#dc2626' : palette.bg,
                              color: confirming ? '#ffffff' : (isDark ? '#fca5a5' : '#b91c1c'),
                              cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
                            }}
                          >
                            {confirming ? 'Confirm cancel' : 'Cancel'}
                          </button>
                          <button
                            onClick={() => handleClear(imp)}
                            disabled={busy}
                            title="Dismiss this notification (does not change the appointment)"
                            style={{
                              fontSize: '0.6875rem', fontWeight: 600, padding: '0.3125rem 0.625rem',
                              borderRadius: '0.4375rem', border: `1px solid ${palette.border}`,
                              background: palette.bgAlt, color: palette.text,
                              cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
                            }}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  const renderFooter = ({ palette }) => (
    onNavigateToAppointments ? (
      <button
        onClick={onNavigateToAppointments}
        style={{
          width: '100%', fontSize: '0.6875rem', fontWeight: 600, padding: '0.4375rem',
          borderRadius: '0.5rem', border: `1px solid ${palette.border}`,
          background: palette.bgAlt, color: palette.accent, cursor: 'pointer',
        }}
      >
        Open Appointments →
      </button>
    ) : null
  );

  return (
    <RoleTaskBadge
      count={total}
      countLabel="IMPACTED"
      title="Confirm Appointments"
      subtitle={subtitle}
      tone="purple"
      verticalAnchorPx={anchorPx}
      ariaLabel={`${total} impacted appointment${total === 1 ? '' : 's'}`}
      metricKey="confirm_appointments"
      footer={renderFooter}
    >
      {renderBody}
    </RoleTaskBadge>
  );
}
